// Recipe catalog for Nexus Forge crafting.
//
// Data-driven: adding a recipe is a config change here, not a code change.
// 5 recipes total: 2 tier-1 (open), 3 tier-2 (skill-gated).
//
// All craft times, input counts, and skill gate thresholds live in
// balance.ts per CONTRACTS §4. Recipes reference them by name.
//
// Quality: the workshop rolls quality per-item at completion time;
// the recipe itself yields 1 item per job at the rolled tier.
// Item IDs in inputs/outputs match shared/items.ts entries (typed
// as string here; narrowed to BaseItemId | CraftableBaseId in Step 4).

import { balance } from "./balance.ts";
import type { SkillId } from "./skills.ts";

// -----------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------

export type RecipeSkill = {
  readonly skillId: SkillId;
  // true  → full primary share (xpPerCraft / primaryCount)
  // false → adjacent share (primary share * adjacentXpRatio)
  readonly primary: boolean;
};

export type SkillGate = {
  readonly skillId: SkillId;
  readonly minXp: number;
};

export type RecipeInput = {
  readonly itemId: string;
  readonly count: number;
};

export type Recipe = {
  readonly id: string;
  readonly name: string;
  // 1 = open (no gate), 2 = gated by skillGates
  readonly tier: 1 | 2; // contracts:allow-literal — type domain, not a tuning value
  readonly inputs: readonly RecipeInput[];
  // base item id without quality suffix; quality is rolled at completion
  readonly outputBaseId: string;
  readonly craftTimeMs: number;
  readonly skills: readonly RecipeSkill[];
  readonly skillGates: readonly SkillGate[];
};

// -----------------------------------------------------------------------
// Catalog
// -----------------------------------------------------------------------

const { craftTimesMs, inputCounts, skillGateXp } = balance.crafting;

// Tier-1: Copper Wire — 3 copper → 1 copper_wire
const copperWire: Recipe = {
  id: "copper_wire",
  name: "Copper Wire",
  tier: 1,
  inputs: [{ itemId: "copper", count: inputCounts.copperWireCopper }],
  outputBaseId: "copper_wire",
  craftTimeMs: craftTimesMs.copperWire,
  skills: [
    { skillId: "crafting", primary: true },
    { skillId: "electronics", primary: false },
  ],
  skillGates: [],
};

// Tier-1: Polymer Plate — 2 polymer → 1 polymer_plate
const polymerPlate: Recipe = {
  id: "polymer_plate",
  name: "Polymer Plate",
  tier: 1,
  inputs: [{ itemId: "polymer", count: inputCounts.polymerPlatePolymer }],
  outputBaseId: "polymer_plate",
  craftTimeMs: craftTimesMs.polymerPlate,
  skills: [{ skillId: "crafting", primary: true }],
  skillGates: [],
};

// Tier-2: Basic Circuit — 2 copper_wire + 1 silicon → 1 basic_circuit
const basicCircuit: Recipe = {
  id: "basic_circuit",
  name: "Basic Circuit",
  tier: 2, // contracts:allow-literal — recipe tier classification
  inputs: [
    { itemId: "copper_wire", count: inputCounts.basicCircuitCopperWire },
    { itemId: "silicon", count: 1 },
  ],
  outputBaseId: "basic_circuit",
  craftTimeMs: craftTimesMs.basicCircuit,
  skills: [
    { skillId: "electronics", primary: true },
    { skillId: "crafting", primary: false },
  ],
  skillGates: [{ skillId: "electronics", minXp: skillGateXp.basicCircuitElectronics }],
};

// Tier-2: Ferrite Core — 3 ferrite + 1 copper_wire → 1 ferrite_core
const ferriteCore: Recipe = {
  id: "ferrite_core",
  name: "Ferrite Core",
  tier: 2, // contracts:allow-literal — recipe tier classification
  inputs: [
    { itemId: "ferrite", count: inputCounts.ferriteCoreFerite },
    { itemId: "copper_wire", count: 1 },
  ],
  outputBaseId: "ferrite_core",
  craftTimeMs: craftTimesMs.ferriteCore,
  skills: [
    { skillId: "crafting", primary: true },
    { skillId: "electronics", primary: false },
  ],
  skillGates: [{ skillId: "crafting", minXp: skillGateXp.ferriteCrafting }],
};

// Tier-1: Repair Kit — 1 polymer + 1 ferrite → 1 repair_kit
// Open to every player (no skill gate); consumed by the Repair tab
// at `balance.combat.repairMaterialCost` kits per weapon repair.
const repairKit: Recipe = {
  id: "repair_kit",
  name: "Repair Kit",
  tier: 1,
  inputs: [
    { itemId: "polymer", count: 1 },
    { itemId: "ferrite", count: 1 },
  ],
  outputBaseId: "repair_kit",
  craftTimeMs: craftTimesMs.polymerPlate, // reuses the tier-1 craft cadence
  skills: [{ skillId: "crafting", primary: true }],
  skillGates: [],
};

// Tier-1: Rusty Knife — 3 ferrite + 1 polymer → 1 rusty_knife
// Starter melee weapon; no gate, same cadence as other tier-1 recipes.
// Completion populates `durability_current = durability_max = 50` on
// the crafted inventory row via `player-inventory.addItem`.
const rustyKnife: Recipe = {
  id: "rusty_knife",
  name: "Rusty Knife",
  tier: 1,
  inputs: [
    { itemId: "ferrite", count: 3 }, // contracts:allow-literal — recipe input count
    { itemId: "polymer", count: 1 },
  ],
  outputBaseId: "rusty_knife",
  craftTimeMs: craftTimesMs.polymerPlate,
  skills: [{ skillId: "crafting", primary: true }],
  skillGates: [],
};

// Tier-1: Scout Pistol — 2 ferrite + 2 copper + 1 polymer → 1 scout_pistol
// Starter ranged weapon; no gate, same cadence as other tier-1 recipes.
const scoutPistol: Recipe = {
  id: "scout_pistol",
  name: "Scout Pistol",
  tier: 1,
  inputs: [
    { itemId: "ferrite", count: 2 }, // contracts:allow-literal — recipe input count
    { itemId: "copper", count: 2 }, // contracts:allow-literal — recipe input count
    { itemId: "polymer", count: 1 },
  ],
  outputBaseId: "scout_pistol",
  craftTimeMs: craftTimesMs.polymerPlate,
  skills: [
    { skillId: "crafting", primary: true },
    { skillId: "electronics", primary: false },
  ],
  skillGates: [],
};

// Tier-2: Signal Amplifier — 1 basic_circuit + 1 ferrite_core + 1 polymer_plate
//         → 1 signal_amplifier
// Both electronics and crafting are primary; XP is split evenly between them.
const signalAmplifier: Recipe = {
  id: "signal_amplifier",
  name: "Signal Amplifier",
  tier: 2, // contracts:allow-literal — recipe tier classification
  inputs: [
    { itemId: "basic_circuit", count: 1 },
    { itemId: "ferrite_core", count: 1 },
    { itemId: "polymer_plate", count: 1 },
  ],
  outputBaseId: "signal_amplifier",
  craftTimeMs: craftTimesMs.signalAmplifier,
  skills: [
    { skillId: "electronics", primary: true },
    { skillId: "crafting", primary: true },
  ],
  skillGates: [
    { skillId: "electronics", minXp: skillGateXp.signalAmplifierElectronics },
    { skillId: "crafting", minXp: skillGateXp.signalAmplifierCrafting },
  ],
};

// Phase 6 Block B: Quantum Filament Brace — 1 quantum_filament + 1 basic_circuit
// → 1 quantum_filament_brace. Gates on crafting + electronics at Journeyman (500 XP).
const quantumFilamentBrace: Recipe = {
  id: "quantum_filament_brace",
  name: "Quantum Filament Brace",
  tier: 2, // contracts:allow-literal — recipe tier classification
  inputs: [
    { itemId: "quantum_filament", count: 1 },
    { itemId: "basic_circuit", count: 1 },
  ],
  outputBaseId: "quantum_filament_brace",
  craftTimeMs: craftTimesMs.quantumFilamentBrace,
  skills: [
    { skillId: "crafting", primary: true },
    { skillId: "electronics", primary: true },
  ],
  skillGates: [
    { skillId: "crafting",    minXp: skillGateXp.quantumFilamentBraceCrafting },
    { skillId: "electronics", minXp: skillGateXp.quantumFilamentBraceElectronics },
  ],
};

// Phase 6 Block B: Plasma Conduit — 1 plasma_core + 1 copper_wire
// → 1 plasma_conduit. Gates on wiring + power-systems at Journeyman.
const plasmaConduit: Recipe = {
  id: "plasma_conduit",
  name: "Plasma Conduit",
  tier: 2, // contracts:allow-literal — recipe tier classification
  inputs: [
    { itemId: "plasma_core", count: 1 },
    { itemId: "copper_wire", count: 1 },
  ],
  outputBaseId: "plasma_conduit",
  craftTimeMs: craftTimesMs.plasmaConduit,
  skills: [
    { skillId: "wiring",         primary: true },
    { skillId: "power-systems",  primary: true },
  ],
  skillGates: [
    { skillId: "wiring",        minXp: skillGateXp.plasmaConduitWiring },
    { skillId: "power-systems", minXp: skillGateXp.plasmaConduitPowerSystems },
  ],
};

// Phase 6 Block B: Reinforced Hull Plate — 3 ferrite + 1 polymer_plate
// → 1 reinforced_hull_plate. Gates on crafting + chemistry at Journeyman.
const reinforcedHullPlate: Recipe = {
  id: "reinforced_hull_plate",
  name: "Reinforced Hull Plate",
  tier: 2, // contracts:allow-literal — recipe tier classification
  inputs: [
    { itemId: "ferrite",       count: 3 }, // contracts:allow-literal — recipe input count
    { itemId: "polymer_plate", count: 1 },
  ],
  outputBaseId: "reinforced_hull_plate",
  craftTimeMs: craftTimesMs.reinforcedHullPlate,
  skills: [
    { skillId: "crafting",   primary: true },
    { skillId: "chemistry",  primary: true },
  ],
  skillGates: [
    { skillId: "crafting",  minXp: skillGateXp.reinforcedHullPlateCrafting },
    { skillId: "chemistry", minXp: skillGateXp.reinforcedHullPlateChemistry },
  ],
};

// -----------------------------------------------------------------------
// Catalog map — keyed by recipe id for O(1) lookup
// -----------------------------------------------------------------------

export const recipeCatalog: Readonly<Record<string, Recipe>> = {
  copper_wire: copperWire,
  polymer_plate: polymerPlate,
  repair_kit: repairKit,
  rusty_knife: rustyKnife,
  scout_pistol: scoutPistol,
  basic_circuit: basicCircuit,
  ferrite_core: ferriteCore,
  signal_amplifier: signalAmplifier,
  quantum_filament_brace: quantumFilamentBrace,
  plasma_conduit: plasmaConduit,
  reinforced_hull_plate: reinforcedHullPlate,
};

// Ordered list for UI display: tier-1 first, then tier-2.
export const recipeList: readonly Recipe[] = [
  copperWire,
  polymerPlate,
  repairKit,
  rustyKnife,
  scoutPistol,
  basicCircuit,
  ferriteCore,
  signalAmplifier,
  quantumFilamentBrace,
  plasmaConduit,
  reinforcedHullPlate,
];

export function getRecipe(id: string): Recipe | undefined {
  return recipeCatalog[id];
}

// Returns true if the player's skill XP map satisfies all gates on a recipe.
// Returns the first recipe whose outputBaseId matches the given base item ID.
export function getRecipeByOutputId(baseItemId: string): Recipe | undefined {
  return recipeList.find((r) => r.outputBaseId === baseItemId);
}

export function meetsSkillGates(
  recipe: Recipe,
  skillXpMap: Readonly<Record<string, number>>,
): boolean {
  return recipe.skillGates.every((gate) => (skillXpMap[gate.skillId] ?? 0) >= gate.minXp);
}
